import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updateProfile,
  User
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { ApiResponse } from '../types';
import { translateError } from '../utils/errorTranslations';


// Cadastrar novo usuário
export const registerUser = async (email: string, password: string, displayName?: string): Promise<ApiResponse<User>> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Se um nome foi fornecido, atualizar o displayName
    if (displayName && displayName.trim()) {
      await updateProfile(userCredential.user, {
        displayName: displayName.trim()
      });
    }
    
    return { success: true, data: userCredential.user };
  } catch (error: any) {
    return { success: false, error: translateError(error) };
  }
};

// Fazer login
export const loginUser = async (email: string, password: string): Promise<ApiResponse<User>> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, data: userCredential.user };
  } catch (error: any) {
    return { success: false, error: translateError(error) };
  }
};

// Fazer logout
export const logoutUser = async (): Promise<ApiResponse<void>> => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: translateError(error) };
  }
};

// Enviar email de redefinição de senha
export const sendPasswordResetEmail = async (email: string): Promise<ApiResponse<void>> => {
  try {
    // Configurar localização para português antes de enviar
    auth.languageCode = 'pt-BR';
    
    await firebaseSendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/login`,
      handleCodeInApp: false
    });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: translateError(error) };
  }
};

// Redefinir senha do usuário logado
export const updateUserPassword = async (currentPassword: string, newPassword: string): Promise<ApiResponse<void>> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'Usuário não está logado' };
    }

    // Reautenticar o usuário com a senha atual
    const credential = EmailAuthProvider.credential(user.email!, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Atualizar a senha
    await updatePassword(user, newPassword);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: translateError(error) };
  }
};

// Observar mudanças no estado de autenticação
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};
